import React, { useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  AppBar,
  Toolbar,
  Select,
  Grid,
  Typography,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import FarmappLogo from "../../assets/logo/FarmAppLogo.png";
import { useAlert } from "../../components/Alert";
import {
  handleMobileKeyDown,
  isNotNullOrEmptyArray,
} from "../../globals/utils";
import instance from "../../globals/axios";
import { compressImage } from "../../globals/utils";
import IconButton from "@mui/material/IconButton";
import CameraIcon from "../../assets/Icon/CameraIcon.png";

const useStyles = makeStyles({
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "400px",
    margin: "0 auto",
    padding: "16px",
  },
  submitButton: {
    marginTop: "16px",
  },
});

const PaymentForm = () => {
  const classes = useStyles();
  const [postOfficeOptions, setPostOfficeOptions] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    farmer_name: "",
    farmer_phone: "",
    farm_area: "",
    app_user: "",
    amount: "",
    status: "PAID",
    payment_type: "BP",
    payment_mode: "UPI",
    tx_number: "",
    tx_proof: null,
    payment_date: "",
    address: {
      state: "",
      district: "",
      postoffice: "",
      village: "",
      address_line1: "",
      address_line2: "",
      pin_code: "",
    },
  });

  const calculateAmount = (farm_area, payment_type) => {
    let amount = "";
    if (farm_area && farm_area > 0) {
      if (payment_type === "ST") {
        amount = 120;
      }
      if (payment_type === "BP") {
        amount = (120 * farm_area).toFixed(2);
      }
      if (payment_type === "PP") {
        amount = (1000 * farm_area).toFixed(2);
      }
      return amount;
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "pin_code") {
      setFormData({
        ...formData,
        address: { ...formData.address, pin_code: value },
      });

      if (value.length === 6) {
        try {
          setLoading(true);
          const response = await instance.get(`common/pincode/${value}`);
          const data = response.data[0];

          if (data.Status === "Success") {
            const { PostOffice } = data;
            if (!isNotNullOrEmptyArray(PostOffice)) {
              console.error("Invalid PIN Code");
              showAlert("error", "Invalid PIN Code");
              return;
            }
            const { State, District, Name } = PostOffice[0];
            setPostOfficeOptions(PostOffice.map((x) => x.Name));
            setFormData({
              ...formData,
              address: {
                ...formData.address,
                pin_code: value,
                state: State,
                district: District,
                postoffice: Name || "",
              },
            });
          } else {
            console.error("Invalid PIN Code");
            showAlert("error", "Invalid PIN Code");
          }
        } catch (error) {
          console.error("Error fetching location details:", error);
        }
        setLoading(false);
      }
    } else if (name === "farmer_phone") {
      setFormData({ ...formData, farmer_phone: value });

      if (value.length === 10) {
        try {
          const response = await instance.get(
            `farmer/get-farmer-details-by-mobile/${value}`
          );
          const { full_name, address_data, id, total_area } =
            response.data.data;
          let amount = calculateAmount(total_area, formData.payment_type);

          setFormData({
            ...formData,
            farmer_name: full_name,
            address: {
              ...formData.address,
              ...address_data,
            },
            app_user: id,
            farmer_phone: value,
            farm_area: total_area || "",
            amount: amount || "",
          });
        } catch (error) {
          console.error("Error fetching farmer details:", error);
        }
      }
    } else if (name == "payment_type") {
      let amount = calculateAmount(formData.farm_area, value);
      setFormData({
        ...formData,
        payment_type: value,
        amount: amount,
      });
    } else if (name === "farm_area") {
      let amount = calculateAmount(value, formData.payment_type);
      setFormData({
        ...formData,
        farm_area: value,
        amount: amount,
      });
    } else if (name in formData.address) {
      setFormData({
        ...formData,
        address: { ...formData.address, [name]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      setFormData((prevData) => ({
        ...prevData,
        [event.target.name]: compressedFile,
      }));
    } catch (error) {
      console.error("Error compressing image:", error);
      showAlert(
        "error",
        "An error occurred while compressing the image. Please try again."
      );
    }
    setSelectedFileName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);

    if (!formData.tx_proof) {
      showAlert("error", "Please upload transaction proof");
      return;
    }

    const _formData = new FormData();
    for (const key in formData) {
      if (key === "address") continue;
      if (["tx_proof"].includes(key) && typeof formData[key] === "string")
        continue;
      _formData.append(key, formData[key]);
    }
    _formData.append("address", JSON.stringify(formData.address));
    try {
      const response = await instance.post(
        "/payments/payment-details/",
        _formData
      );
      console.log("Form submitted successfully:", response.data);
      showAlert("success", "Payment details submitted successfully");
      alert("Payment details submitted successfully");
      window.location.reload();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ padding: "10px 0" }}
      >
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src={FarmappLogo}
              alt="Farmapp Logo"
              style={{ height: "60px", width: "auto" }}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={handleSubmit}
      >
        <TextField
          label="Farmer Phone"
          name="farmer_phone"
          value={formData.farmer_phone}
          onChange={handleChange}
          required
          fullWidth
          type="tel"
          inputProps={{
            maxLength: 10,
            inputMode: "numeric",
          }}
          onKeyDown={handleMobileKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">+91</InputAdornment>
            ),
          }}
        />

        <TextField
          label="Farmer Name"
          name="farmer_name"
          value={formData.farmer_name}
          onChange={handleChange}
          required
          fullWidth
        />

        <TextField
          label="Farm Area (in acres)"
          name="farm_area"
          type="number"
          value={formData.farm_area}
          onChange={handleChange}
          required
          fullWidth
        />

        <TextField
          label="Payment Type"
          name="payment_type"
          select
          value={formData.payment_type}
          onChange={handleChange}
          fullWidth
        >
          <MenuItem value="ST">Soil Test</MenuItem>
          <MenuItem value="BP">Basic Plan</MenuItem>
          <MenuItem value="PP">Premium Plan</MenuItem>
        </TextField>

        <TextField
          label="Amount (Rs.)"
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          required
          fullWidth
          helperText="Calculate Amount is based on Farm Area and Payment Type"
        />

        <TextField
          label="Status"
          name="status"
          select
          value={formData.status}
          onChange={handleChange}
          fullWidth
        >
          <MenuItem value="UNPAID">Unpaid</MenuItem>
          <MenuItem value="PAID">Paid</MenuItem>
        </TextField>

        <TextField
          label="Payment Mode"
          name="payment_mode"
          select
          value={formData.payment_mode}
          onChange={handleChange}
          fullWidth
        >
          <MenuItem value="UPI">UPI</MenuItem>
        </TextField>

        <TextField
          label="UPI ID"
          name="tx_number"
          value={formData.tx_number}
          onChange={handleChange}
          fullWidth
          required
        />

        <Grid container sx={{ marginTop: "10px" }}>
          <Grid item xs={5}>
            <Typography style={{ fontSize: "12px", fontWeight: 600 }}>
              Add Transaction Proof
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <IconButton sx={{ padding: "0px" }}>
              <img src={CameraIcon} style={{ width: "50px", height: "47px" }} />
            </IconButton>
          </Grid>
          <Grid item xs={5}>
            <input
              type="file"
              id="tx-proof"
              name="tx_proof"
              accept="image/*"
              onChange={onFileChange}
              style={{ display: "none" }}
            />
            <Button
              variant="contained"
              component="label"
              style={{
                marginLeft: "auto",
                color: "white",
                width: "100%",
                height: "33px",
              }}
              htmlFor="tx-proof"
            >
              Upload
            </Button>
          </Grid>
        </Grid>

        {selectedFileName && <Typography>{selectedFileName}</Typography>}

        <TextField
          label="Payment Date"
          name="payment_date"
          type="date"
          value={formData.payment_date}
          onChange={handleChange}
          required
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <TextField
          label="PIN Code"
          name="pin_code"
          value={formData.address.pin_code}
          onChange={handleChange}
          fullWidth
        />

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <TextField
              label="State"
              name="state"
              value={formData.address.state}
              onChange={handleChange}
              required
              fullWidth
              disabled
            />

            <TextField
              label="District"
              name="district"
              value={formData.address.district}
              onChange={handleChange}
              required
              fullWidth
              disabled
            />
            <Select
              label="Post Office"
              name="postoffice"
              value={formData.address.postoffice}
              onChange={handleChange}
              fullWidth
            >
              {postOfficeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </>
        )}

        <TextField
          label="Village"
          name="village"
          value={formData.address.village}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Address Line 1"
          name="address_line1"
          value={formData.address.address_line1}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label="Address Line 2"
          name="address_line2"
          value={formData.address.address_line2}
          onChange={handleChange}
          fullWidth
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitButton}
        >
          Submit
        </Button>
      </Box>
    </>
  );
};

export default PaymentForm;
